import React, { useRef, useEffect } from 'react';
import { motion, useSpring } from 'motion/react';

interface MagneticButtonProps {
  children: React.ReactNode;
  className?: string;
}

export function MagneticButton({ children, className = '' }: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const cachedCenterRef = useRef<{ x: number; y: number } | null>(null);

  const springX = useSpring(0, { stiffness: 100, damping: 20, mass: 0.1 });
  const springY = useSpring(0, { stiffness: 100, damping: 20, mass: 0.1 });

  useEffect(() => {
    const invalidateRect = () => {
      cachedCenterRef.current = null;
    };
    window.addEventListener('scroll', invalidateRect, { passive: true });
    window.addEventListener('resize', invalidateRect, { passive: true });
    return () => {
      window.removeEventListener('scroll', invalidateRect);
      window.removeEventListener('resize', invalidateRect);
    };
  }, []);

  const measureRestingCenter = () => {
    if (!ref.current) return;
    const { height, width, left, top } = ref.current.getBoundingClientRect();
    const currentX = springX.get();
    const currentY = springY.get();
    cachedCenterRef.current = {
      x: left + width / 2 - currentX,
      y: top + height / 2 - currentY,
    };
  };

  const handleMouseEnter = () => {
    measureRestingCenter();
  };

  const handleMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cachedCenterRef.current) {
      measureRestingCenter();
    }
    if (!cachedCenterRef.current) return;

    const middleX = e.clientX - cachedCenterRef.current.x;
    const middleY = e.clientY - cachedCenterRef.current.y;

    springX.set(middleX * 0.15);
    springY.set(middleY * 0.15);
  };

  const reset = () => {
    springX.set(0);
    springY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      className={className}
      style={{
        x: springX,
        y: springY,
      }}
    >
      {children}
    </motion.div>
  );
}
